import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSecurityMaster } from "../../context/SecurityMasterContext";
import { useMarketIndicators } from "../../context/MarketIndicatorContext";
import { useWatchlist } from "../../context/WatchlistContext";
import { useIndicators } from "../../context/IndicatorContext";
// import { useStockName } from "../../context/StockNameContext";
import { useMoreStockContext } from "../../context/MoreStockContext";
import { useMarketToggle } from "../../context/MarketContext";
import { useAuth } from "../../context/AuthNewContext";
import Gauge from "../Gauge/Gauge";
import { FaSpinner } from "react-icons/fa6";
import { FiRefreshCw } from "react-icons/fi";
import { useAdmin } from "../../context/AdminContext";
import { useVariant } from "../../hooks/useVariant";
import { useAIChat } from "../../context/AIChatContext";
import { FiStar } from "react-icons/fi"; // outline
import { useMarket } from "../../context/MarketDhanContext";
import { useChartIndicator } from "../../context/ChartIndicatorContext";
import { FaStar, FaRegStar } from "react-icons/fa"; // filled

import UpgradeModal from "../UpgradeModal/UpgradeModal";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
  ComposedChart,
} from "recharts";

import { useOption } from "../../context/OptionContext";

import RangeBarSVG from "../RangeSvg/RangeBarSVG";
import ChatPanel from "../ChatPanel/ChatPanel";
import AIFloatButton from "../AIFloatButton/AIFloatButton";
import Chat from "../Chat/Chat";
import { usePdf } from "../../context/PdfContext";
import ChatPanel2 from "../ChatPanel2/ChatPanel2";
import CandleChart from "../CandleChart/CandleChart";

const StockCard = () => {
  // ============================================================
  // STATE
  // ============================================================
  const [securityId, setSecurityId] = useState(null);
  const [loadingSecurity, setLoadingSecurity] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [lastUTSignal, setLastUTSignal] = useState(null);
  const [crossovers, setCrossovers] = useState([]);
  const [error, setError] = useState("");
  const [interval, setInterval] = useState("1d");
  const [position, setPosition] = useState({
    inPosition: false,
    entryPrice: null,
  });
  const [lastUpdated, setLastUpdated] = useState("");
  const [liveReady, setLiveReady] = useState(false);
  const [visibleCount, setVisibleCount] = useState(8);
  const [timeFrame, setTimeFrame] = useState("min");
  const [resistTimeFrame, setResistTimeFrame] = useState("day");

  // ============================================================
  // HOOKS
  // ============================================================
  const { fetchBySymbol, pcr } = useOption();
  const { setMoreData } = useMoreStockContext();
  const { fetchNewsData, newsData } = useIndicators();
  const { getSecurityIdBySymbol } = useMarket();
  const { getChartIndicators, chartIndicatorData } = useChartIndicator();
  const { downloadStockPDF } = usePdf();
  const {
    indicatorData,
    fetchIndicators,
    getPrevClose,
    previousClose,
    quoteData,
    getMarketQuote,
    getMarketIntraday,
    intradayData,
    getTargetPrice,
    resetTargetPrice,
    targetPrice,
    getMarketCap,
    marketCapData,
    getStockStatistics,
    statsData,
    getReturnData,
    returnData,
    getNameBySymbol,
    stockName,
    getSma,
    maData,
    availableCandles,
    resetMarketData,
  } = useMarketIndicators();
  const { user } = useAuth();
  const userID = user?.userID;
  const { companyCode } = useParams();
  const navigate = useNavigate();
  const variant = useVariant();
  const { chatOpen } = useAIChat();
  const { addToWatchlist, removeFromWatchlist, watchlist, isInWatchlist } =
    useWatchlist();
  const { market } = useMarketToggle();

  // ============================================================
  // UTILITY
  // ============================================================
  const formatTimestamp = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    const time = now.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    return `${day}-${month}-${year} ${time} IST`;
  };

  // ============================================================
  // STEP 1: loadSingleStock — NOW RETURNS THE ID DIRECTLY
  // This is the core fix. Previously it only called setSecurityId(id)
  // and returned nothing. The useEffect watching securityId would
  // fire before the state flushed, seeing undefined and skipping.
  // Now we return the id so the bootstrap chain can use it immediately.
  // ============================================================
  const loadSingleStock = async () => {
    try {
      setLoadingSecurity(true);
      setError(null);

      const res = await getSecurityIdBySymbol(companyCode);
      const id = Number(res?.security_id) || null;

      if (!id) {
        setError("Security ID not found");
        setLoadingSecurity(false);
        return null; // ✅ return null so caller knows it failed
      }

      setSecurityId(id); // still set for other parts of UI that read securityId
      return id; // ✅ KEY FIX: also return it directly
    } catch (err) {
      console.error("Security ID Error", err);
      setError("Failed to load security ID");
      setLoadingSecurity(false);
      return null;
    }
  };

  // ============================================================
  // STEP 2: loadLiveData — NOW ACCEPTS id AS PARAMETER
  // Previously defined INSIDE a useEffect watching securityId.
  // That meant it only ran after setSecurityId flushed (which
  // was too late on first load). Now it accepts id directly
  // so it can be called right after loadSingleStock returns.
  // ============================================================
  const loadLiveData = async (id) => {
    if (!id) return;

    try {
      setLoadingSecurity(true);
      setLiveReady(false);
      setError(null);

      const tasks = [
        { key: "indicators", fn: () => fetchIndicators(id) },
        { key: "prevClose", fn: () => getPrevClose(id) },
        {
          key: "quote",
          fn: () =>
            getMarketQuote({
              securityId: Number(id),
              symbol: companyCode,
              userID,
            }),
        },
        { key: "intraday", fn: () => getMarketIntraday(id) },
        { key: "returns", fn: () => getReturnData(id) },
        { key: "sma", fn: () => getSma(id) },
      ];

      const results = await Promise.allSettled(tasks.map((t) => t.fn()));

      const resultMap = {};
      results.forEach((res, idx) => {
        resultMap[tasks[idx].key] = res;
      });

      const quoteResult = resultMap.quote;

      if (quoteResult.status === "rejected") {
        const err = quoteResult.reason;
        if (err?.response?.data?.type === "TRIAL_LIMIT") {
          setShowUpgradeModal(true);
          setLiveReady(true);
          setLoadingSecurity(false);
          setError(null);
          return;
        }
        setError(err?.response?.data?.message || "Failed to load market data");
        setLiveReady(false);
        setLoadingSecurity(false);
        return;
      }

      const value = quoteResult.value;

      if (value?.type === "TRIAL_LIMIT") {
        setShowUpgradeModal(true);
        setLiveReady(true);
        setLoadingSecurity(false);
        setError(null);
        return;
      }

      if (!value?.data || Object.keys(value.data).length === 0) {
        setError("Market data unavailable for this stock");
        setLiveReady(false);
        setLoadingSecurity(false);
        return;
      }

      Object.entries(resultMap).forEach(([key, res]) => {
        if (res.status === "rejected") {
          console.warn(`⚠️ ${key} API failed`);
        }
      });

      setError(null);
      setLastUpdated(formatTimestamp());
      setLiveReady(true);
    } catch (err) {
      console.error("Live Data Crash", err);
      setError("Unexpected market error");
      setLiveReady(false);
    } finally {
      setLoadingSecurity(false);
    }
  };

  // ============================================================
  // STEP 3: handleRefresh — uses existing securityId from state
  // ============================================================
  const handleRefresh = async () => {
    if (!securityId) return;
    fetchBySymbol(companyCode);
    fetchNewsData(companyCode);
    await loadLiveData(securityId);
  };

  // ============================================================
  // STEP 4: MAIN BOOTSTRAP useEffect
  // This is the ONLY useEffect that triggers data loading.
  // The old broken pattern was:
  //   useEffect([companyCode]) → loadSingleStock() [fire and forget]
  //   useEffect([securityId])  → loadLiveData()    [missed on first load]
  //
  // The new pattern is one sequential async chain:
  //   useEffect([companyCode]) → bootstrap() →
  //     id = await loadSingleStock()  [get id directly]
  //     await loadLiveData(id)        [use id immediately, no state wait]
  //     fetchNewsData + getChartIndicators [also use id directly]
  // ============================================================
  useEffect(() => {
    if (!companyCode) return;

    let cancelled = false;

    // Reset everything when stock changes
    setError(null);
    setLiveReady(false);
    setLoadingSecurity(true);
    setSecurityId(null);
    resetTargetPrice();
    resetMarketData();

    // Fire non-live calls immediately — these don't need securityId
    getNameBySymbol(companyCode);
    getMarketCap(companyCode);
    fetchBySymbol(companyCode);
    getTargetPrice(companyCode, market);
    getStockStatistics(companyCode);

    const bootstrap = async () => {
      // GET THE ID AND USE IT DIRECTLY — no waiting for state flush
      const id = await loadSingleStock();

      if (!id || cancelled) return;

      // Pass id directly — this is what fixes the first-load bug
      await loadLiveData(id);

      if (cancelled) return;

      // These also need id — pass directly
      fetchNewsData(companyCode);
      getChartIndicators({
        symbol: companyCode,
        securityId: id,
        interval: "5",
      });
    };

    bootstrap();

    // Cleanup: if user navigates away before load finishes
    return () => {
      cancelled = true;
    };
  }, [companyCode]); // Only re-run when route changes

  // ============================================================
  // DERIVED DATA — safe guards added
  // ============================================================
  const quote = quoteData?.[securityId];
  const stock = quote?.ohlc;
  const prev_close = previousClose?.prevClose;
  const ltp = Number(quote?.last_price);

  const percent_change = (
    ((quote?.last_price - prev_close) / prev_close) *
    100
  ).toFixed(2);

  const depth = quote?.depth;
  const totalBuyQty =
    depth?.buy?.reduce((sum, b) => sum + (b.quantity || 0), 0) || 0;
  const totalSellQty =
    depth?.sell?.reduce((sum, s) => sum + (s.quantity || 0), 0) || 0;
  const totalQty = totalBuyQty + totalSellQty;
  const buyPercent = totalQty ? (totalBuyQty / totalQty) * 100 : 0;
  const sellPercent = totalQty ? (totalSellQty / totalQty) * 100 : 0;

  const weekLow = quote?.["52_week_low"];
  const weekHigh = quote?.["52_week_high"];

  const latestRSIValue =
    indicatorData?.indicators?.rsi?.length > 0
      ? indicatorData.indicators.rsi.at(-1).value
      : null;
  const latestMACDValue =
    indicatorData?.indicators?.macd?.length > 0
      ? indicatorData.indicators.macd.at(-1).macd
      : null;

  const dates = indicatorData?.dates;
  const indicators = indicatorData?.indicators;
  const indicatorsMin = intradayData?.indicators;
  const masterTimelineMin = intradayData?.closeSeries || [];
  const masterTimelineDay = indicatorData?.closeSeries || [];

  // ✅ SAFE GUARD — previously crashed when indicatorsMin was null
  const macdChartDataMin = useMemo(() => {
    if (!masterTimelineMin.length || !indicatorsMin?.macd?.length) return [];
    return masterTimelineMin.map((p, i) => {
      const offset = masterTimelineMin.length - indicatorsMin.macd.length;
      const macdIndex = i - offset;
      if (macdIndex < 0)
        return { date: p.date, MACD: null, signal: null, histogram: null };
      const m = indicatorsMin.macd[macdIndex];
      return {
        date: p.date,
        MACD: m.macd,
        signal: m.signal,
        histogram: m.histogram,
      };
    });
  }, [masterTimelineMin, indicatorsMin]);

  // ✅ SAFE GUARD — previously crashed when indicators was null
  const macdChartData = useMemo(() => {
    if (!masterTimelineDay.length || !indicators?.macd?.length) return [];
    return masterTimelineDay.map((p, i) => {
      const offset = masterTimelineDay.length - indicators.macd.length;
      const macdIndex = i - offset;
      if (macdIndex < 0)
        return { date: p.date, MACD: null, signal: null, histogram: null };
      const m = indicators.macd[macdIndex];
      return {
        date: p.date,
        MACD: m.macd,
        signal: m.signal,
        histogram: m.histogram,
      };
    });
  }, [masterTimelineDay, indicators]);

  const bbgSignal = chartIndicatorData?.lastSignal?.text;

  // ============================================================
  // TARGET PRICE
  // ============================================================
  const rawPrice = targetPrice?.data?.target_price;
  const target_price = rawPrice
    ? Number(String(rawPrice).replace(/,/g, ""))
    : 0;
  const rawLowPrice = targetPrice?.data?.target_low;
  const target_low = rawLowPrice
    ? Number(String(rawLowPrice).replace(/,/g, ""))
    : 0;
  const rawHighPrice = targetPrice?.data?.target_high;
  const target_high = rawHighPrice
    ? Number(String(rawHighPrice).replace(/,/g, ""))
    : 0;

  const projectChange = target_price
    ? (((target_price - quote?.last_price) / quote?.last_price) * 100).toFixed(
        2,
      )
    : null;

  const tLow = Number(target_low);
  const tHigh = Number(target_high);
  const tMean = Number(target_price);

  const statisticsData = statsData?.data?.body;
  const peRatio = statsData?.data?.body?.forwardPE?.fmt;

  // ============================================================
  // RETURNS
  // ============================================================
  const calcReturn = (current, past) =>
    past ? (((current - past) / past) * 100).toFixed(2) : null;

  const oneWeekReturn = calcReturn(
    quote?.last_price,
    returnData?.prices?.oneWeek,
  );
  const oneMonthReturn = calcReturn(
    quote?.last_price,
    returnData?.prices?.oneMonth,
  );
  const ytdReturn = calcReturn(quote?.last_price, returnData?.prices?.ytd);
  const oneYearReturn = calcReturn(
    quote?.last_price,
    returnData?.prices?.oneYear,
  );
  const threeYearReturn = calcReturn(
    quote?.last_price,
    returnData?.prices?.threeYear,
  );
  const fiveYearReturn = calcReturn(
    quote?.last_price,
    returnData?.prices?.fiveYear,
  );

  // ============================================================
  // RESISTANCE / PIVOT DATA
  // ============================================================
  const resistData = previousClose?.hist;
  const resistLow = resistData?.low;
  const resistHigh = resistData?.high;
  const resistClose = resistData?.close;
  const resistOpen = resistData?.open;

  const resDailyLow = resistLow?.[resistLow?.length - 1];
  const resWeeklyLow = resistLow?.[0];
  const resDailyHigh = resistHigh?.[resistHigh?.length - 1];
  const resWeeklyHigh = resistHigh?.[0];
  const resDailyClose = resistClose?.[resistClose?.length - 1];
  const resWeeklyClose = resistClose?.[0];
  const resDailyOpen = resistOpen?.[resistOpen?.length - 1];
  const resWeeklyOpen = resistOpen?.[0];

  const calculatePivots = (open, high, low, close) => {
    if (!open || !high || !low || !close)
      return { classic: {}, fib: {}, cam: {}, woodie: {}, demark: {} };
    const range = high - low;
    const classicP = (high + low + close) / 3;
    const classic = {
      R3: high + 2 * (classicP - low),
      R2: classicP + range,
      R1: 2 * classicP - low,
      P: classicP,
      S1: 2 * classicP - high,
      S2: classicP - range,
      S3: low - 2 * (high - classicP),
    };
    const fib = {
      R1: classicP + range * 0.382,
      R2: classicP + range * 0.618,
      R3: classicP + range * 1.0,
      P: classicP,
      S1: classicP - range * 0.382,
      S2: classicP - range * 0.618,
      S3: classicP - range * 1.0,
    };
    const cam = {
      R1: close + range * 1.0833,
      R2: close + range * 1.1666,
      R3: close + range * 1.25,
      P: close,
      S1: close - range * 1.0833,
      S2: close - range * 1.1666,
      S3: close - range * 1.25,
    };
    const woodieP = (high + low + 2 * close) / 4;
    const woodie = {
      R1: 2 * woodieP - low,
      R2: woodieP + range,
      R3: high + 2 * (woodieP - low),
      P: woodieP,
      S1: 2 * woodieP - high,
      S2: woodieP - range,
      S3: low - 2 * (high - woodieP),
    };
    let demPivot;
    if (close < open) demPivot = (high + 2 * low + close) / 4;
    else if (close > open) demPivot = (2 * high + low + close) / 4;
    else demPivot = (high + low + 2 * close) / 4;
    const demark = { P: demPivot, R1: demPivot + range, S1: demPivot - range };
    return { classic, fib, cam, woodie, demark };
  };

  // ✅ Memoized — was recalculating on every render before
  const resDaily = useMemo(
    () =>
      calculatePivots(resDailyOpen, resDailyHigh, resDailyLow, resDailyClose),
    [resDailyOpen, resDailyHigh, resDailyLow, resDailyClose],
  );

  const resWeekly = useMemo(
    () =>
      calculatePivots(
        resWeeklyOpen,
        resWeeklyHigh,
        resWeeklyLow,
        resWeeklyClose,
      ),
    [resWeeklyOpen, resWeeklyHigh, resWeeklyLow, resWeeklyClose],
  );

  const levels = [
    { key: "R3", label: "Third Resistance" },
    { key: "R2", label: "Second Resistance" },
    { key: "R1", label: "First Resistance" },
    { key: "P", label: "Pivot" },
    { key: "S1", label: "First Support" },
    { key: "S2", label: "Second Support" },
    { key: "S3", label: "Third Support" },
  ];

  const resistanceDataToRender =
    resistTimeFrame === "day" ? resDaily : resWeekly;

  // ============================================================
  // MA ROWS — already memoized, keeping as-is
  // ============================================================
  const buildMARows = (ltp, maData = []) => {
    if (!ltp || !Array.isArray(maData)) return [];
    return maData.map((ma) => ({
      name: ma?.name || "-",
      smaValue: Number(ma?.sma || 0),
      emaValue: Number(ma?.ema || 0),
      smaTrend: ltp >= ma?.sma ? "Buy" : "Sell",
      emaTrend: ltp >= ma?.ema ? "Buy" : "Sell",
    }));
  };

  const buildMASummary = (rows) => {
    const finalBuy =
      rows?.filter((r) => r?.smaTrend === "Buy").length +
      rows?.filter((r) => r?.emaTrend === "Buy").length;
    const finalSell =
      rows?.filter((r) => r?.smaTrend === "Sell").length +
      rows?.filter((r) => r?.emaTrend === "Sell").length;
    return { finalBuy, finalSell };
  };

  const rows = useMemo(() => {
    if (!ltp || !maData?.length) return [];
    return buildMARows(ltp, maData);
  }, [ltp, maData]);

  const summary = useMemo(() => buildMASummary(rows), [rows]);

  // ============================================================
  // CANDLES + VWAP — already memoized, keeping as-is
  // ============================================================
  const candles = useMemo(() => {
    const data = intradayData?.intraday;
    if (!data || !Array.isArray(data.close)) return [];
    return data.close.map((price, i) => ({
      close: Number(price) || 0,
      volume: Number(data.volume?.[i]) || 0,
    }));
  }, [intradayData]);

  const calculateVWAP = (data = []) => {
    if (!Array.isArray(data) || data.length === 0) return [];
    let cumulativePV = 0,
      cumulativeVolume = 0;
    return data.map((candle) => {
      const price = Number(candle?.close) || 0;
      const volume = Number(candle?.volume) || 0;
      cumulativePV += price * volume;
      cumulativeVolume += volume;
      const vwap =
        cumulativeVolume === 0 ? null : cumulativePV / cumulativeVolume;
      return { ...candle, vwap };
    });
  };

  const vwapData = useMemo(() => calculateVWAP(candles), [candles]);
  const latestVWAP = vwapData?.at(-1)?.vwap;
  const vwap = latestVWAP;
  const ltpValue = ltp;
  const diff = vwap && ltpValue ? ltpValue - vwap : 0;
  const percent = vwap ? ((diff / vwap) * 100).toFixed(2) : 0;
  const signal =
    ltpValue > vwap ? "Bullish" : ltpValue < vwap ? "Bearish" : "Neutral";

  // ============================================================
  // INTERPRETATION
  // ============================================================
  const getInterpretation = (indicators) => {
    if (
      !indicators ||
      !Array.isArray(indicators.macd) ||
      !Array.isArray(indicators.rsi) ||
      indicators.macd.length === 0 ||
      indicators.rsi.length === 0
    ) {
      return {
        result: "Currently data not available. Kindly Refresh again",
        color: "#fbbf24",
        bgColor: "#FFF5CC",
        prediction: "Neutral",
      };
    }
    const latestRSI = indicators.rsi.at(-1)?.value;
    const macd = indicators.macd.at(-1)?.macd;
    const sig = indicators.macd.at(-1)?.signal;
    const histogram = indicators.macd.at(-1)?.histogram;
    if ([latestRSI, macd, sig, histogram].some((v) => typeof v !== "number")) {
      return {
        result: "Indicator data incomplete",
        color: "#fbbf24",
        bgColor: "#FFF5CC",
        prediction: "Neutral",
      };
    }
    let rsiStatus = "Neutral";
    if (latestRSI >= 70) rsiStatus = "Overbought";
    else if (latestRSI <= 30) rsiStatus = "Oversold";
    else if (latestRSI > 50) rsiStatus = "Neutral-Bullish";
    else rsiStatus = "Neutral-Bearish";
    let macdStatus = "Neutral";
    if (macd > sig && histogram > 0) macdStatus = "Bullish";
    else if (macd < sig && histogram < 0) macdStatus = "Bearish";
    if (latestRSI > 55 && macdStatus === "Bullish")
      return {
        result: "Strong Bullish Momentum (RSI & MACD aligned)",
        color: "#249966",
        bgColor: "#D4F7E6",
        prediction: "Bullish",
      };
    if (latestRSI < 45 && macdStatus === "Bearish")
      return {
        result: "Strong Bearish Momentum (RSI & MACD aligned)",
        color: "#f87171",
        bgColor: "#FFE1E1",
        prediction: "Bearish",
      };
    if (
      (latestRSI > 50 && macd < sig && histogram < 0) ||
      (latestRSI < 50 && macd > sig && histogram > 0)
    )
      return {
        result: "Divergence detected – Possible reversal",
        color: "#fbbf24",
        bgColor: "#FFF5CC",
        prediction: "Neutral",
      };
    if (rsiStatus === "Overbought" && macdStatus === "Bullish")
      return {
        result: "Overbought – Bullish but tiring",
        color: "#f59e0b",
        bgColor: "#FFF5CC",
        prediction: "Bearish",
      };
    if (rsiStatus === "Oversold" && macdStatus === "Bearish")
      return {
        result: "Oversold – Bearish but rebound possible",
        color: "#60a5fa",
        bgColor: "#DBEAFE",
        prediction: "Bullish",
      };
    return {
      result: "Neutral / Sideways Market",
      color: "#9ca3af",
      bgColor: "#E5E7EB",
      prediction: "Neutral",
    };
  };

  const { result, color, bgColor, prediction } = getInterpretation(indicators);
  const {
    result: result1,
    color: color1,
    prediction: prediction1,
  } = getInterpretation(indicatorsMin);

  // ============================================================
  // PIVOT NEAREST LEVELS
  // ============================================================
  const getNearestLevels = (levelsObj, ltp) => {
    if (!levelsObj || !ltp) return {};
    let nearestResistance = null,
      nearestSupport = null;
    let minResDiff = Infinity,
      minSupDiff = Infinity;
    Object.entries(levelsObj).forEach(([key, value]) => {
      if (!value) return;
      const diff = value - ltp;
      if (key.startsWith("R") && diff > 0 && diff < minResDiff) {
        minResDiff = diff;
        nearestResistance = { key, value };
      }
      if (key.startsWith("S") && diff < 0 && Math.abs(diff) < minSupDiff) {
        minSupDiff = Math.abs(diff);
        nearestSupport = { key, value };
      }
    });
    return { nearestResistance, nearestSupport };
  };

  const { nearestResistance, nearestSupport } = useMemo(
    () => getNearestLevels(resistanceDataToRender?.classic, ltp),
    [resistanceDataToRender, ltp],
  );

  // ============================================================
  // TRADING ANALYSIS — keeping your existing runTradingAnalysis
  // but fixing the position loop with a ref
  // ============================================================
  const positionRef = useRef(position);

  const runTradingAnalysis = ({
    ltp,
    vwap,
    prevClose,
    prevVWAP,
    volume,
    avgVolume,
    r1,
    s1,
    rLabel = "R1",
    sLabel = "S1",
    position,
  }) => {
    if (!ltp || !vwap) return {};
    const ENTRY_ZONE_PCT = 0.002,
      STOP_BUFFER = 0.001,
      TARGET_PCT = 0.005;
    let entry = {},
      sig = null,
      exit = null,
      newPosition = position;
    const entryZoneUpper = vwap * (1 + ENTRY_ZONE_PCT);
    const isNearVWAP = ltp >= vwap && ltp <= entryZoneUpper;
    const isReclaim = prevClose < prevVWAP && ltp > vwap;
    const isHighVolume = !volume || !avgVolume || volume > avgVolume * 0.8;
    if (!position?.inPosition) {
      if (isReclaim && isHighVolume) {
        entry = {
          label: "VWAP Reclaim Buy",
          msg: "Strong breakout above VWAP",
          type: "buy",
        };
        sig = `BUY (Reclaim) @ ₹${ltp}`;
        newPosition = { inPosition: true, entryPrice: Number(ltp.toFixed(2)) };
      } else if (isNearVWAP) {
        entry = {
          label: "Pullback Buy",
          msg: "VWAP Support Zone",
          type: "buy",
        };
        sig = `BUY (Pullback) @ ₹${ltp}`;
        newPosition = { inPosition: true, entryPrice: Number(ltp.toFixed(2)) };
      } else {
        entry = {
          label: "No Trade",
          msg: "Wait for VWAP setup",
          type: "neutral",
        };
      }
    } else if (position?.inPosition) {
      const entryPrice = position.entryPrice;
      const target = entryPrice * (1 + TARGET_PCT);
      const stopLoss = vwap * (1 - STOP_BUFFER);
      if (ltp >= target) {
        exit = `Target Hit 🚀 SELL @ ₹${ltp}`;
        newPosition = { inPosition: false, entryPrice: null };
      } else if (ltp < stopLoss) {
        exit = `VWAP Breakdown ❌ SELL @ ₹${ltp}`;
        newPosition = { inPosition: false, entryPrice: null };
      }
      entry = {
        label: "In Trade",
        msg: `Entry ₹${entryPrice} | LTP ₹${ltp}`,
        type: "active",
      };
    }
    if (!sig && !position?.inPosition) {
      if (ltp > vwap) {
        sig = `BUY (Fallback) @ ₹${ltp}`;
        entry = {
          label: "Fallback Buy",
          msg: "Above VWAP (fallback)",
          type: "buy",
        };
        newPosition = position;
      } else if (ltp < vwap) {
        sig = `SELL (Fallback) @ ₹${ltp}`;
        entry = {
          label: "Fallback Sell",
          msg: "Below VWAP (fallback)",
          type: "sell",
        };
        newPosition = position;
      }
    }
    if (position?.inPosition && !exit) exit = `HOLD → LTP ₹${ltp}`;
    if (!sig && position?.inPosition) sig = "IN TRADE";
    const targets = {
      primary:
        ltp > vwap
          ? { label: rLabel, value: r1 }
          : { label: sLabel, value: s1 },
    };
    const stopLossDisplay =
      ltp > vwap
        ? (vwap * (1 - STOP_BUFFER)).toFixed(2)
        : (vwap * (1 + STOP_BUFFER)).toFixed(2);
    const TARGET_PCT_PY = 0.01;
    const entryZoneUpperPy = vwap * (1 + ENTRY_ZONE_PCT);
    let pythonSignal = null,
      pythonTrend = null;
    if (!position?.inPosition) {
      if (ltp >= vwap && ltp <= entryZoneUpperPy) {
        pythonSignal = `BUY at ₹${ltp}, VWAP ₹${vwap.toFixed(2)}`;
        pythonTrend = "BULLISH ENTRY";
      } else if (ltp > vwap) {
        pythonSignal = `LTP ₹${ltp} is above VWAP`;
        pythonTrend = "TRENDING BULLISH";
      }
    } else if (position?.inPosition) {
      const entryPrice = position.entryPrice;
      if (ltp >= entryPrice * (1 + TARGET_PCT_PY)) {
        pythonSignal = `SELL at ₹${ltp}, Gain: 1%`;
        pythonTrend = "EXIT PROFIT";
      } else if (ltp < vwap) {
        pythonSignal = `LOSS SELL at ₹${ltp}, Below VWAP`;
        pythonTrend = "EXIT STOP";
      }
    }
    const traderNote = position?.inPosition
      ? `IN TRADE → Entry ₹${position.entryPrice} | Target ₹${(position.entryPrice * (1 + TARGET_PCT_PY)).toFixed(2)}`
      : pythonSignal || `WAIT → VWAP ₹${vwap.toFixed(2)}`;
    return {
      entry,
      signal: sig,
      exit,
      targets,
      stopLoss: stopLossDisplay,
      traderNote,
      newPosition,
      pythonSignal,
      pythonTrend,
    };
  };

  const analysis = useMemo(() => {
    if (!quote || !latestVWAP) return null;
    return runTradingAnalysis({
      ltp: quote?.last_price,
      vwap: latestVWAP,
      prevClose: prev_close || quote?.last_price,
      prevVWAP: latestVWAP,
      volume: quote?.volume ?? 0,
      avgVolume: 0,
      r1: nearestResistance?.value,
      s1: nearestSupport?.value,
      rLabel: nearestResistance?.key,
      sLabel: nearestSupport?.key,
      position,
    });
  }, [
    quote?.last_price,
    latestVWAP,
    nearestResistance,
    nearestSupport,
    position,
  ]);

  // ✅ Fixed position loop — uses ref to prevent infinite re-renders
  useEffect(() => {
    if (!analysis?.newPosition) return;
    const prev = positionRef.current || {};
    const next = analysis.newPosition || {};
    const isSame =
      prev.inPosition === next.inPosition &&
      Number(prev.entryPrice || 0).toFixed(2) ===
        Number(next.entryPrice || 0).toFixed(2);
    if (!isSame) {
      positionRef.current = next;
      setPosition(next);
    }
  }, [analysis?.newPosition]);

  // ============================================================
  // CHART DATA SELECTION
  // ============================================================
  const resampleMinuteMACD = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;
    const result = [];
    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;
      const last = chunk[chunk.length - 1];
      result.push({
        date: last.date,
        MACD: last.MACD,
        signal: last.signal,
        histogram: chunk.reduce((sum, c) => sum + (c.histogram ?? 0), 0),
      });
    }
    return result;
  };

  const resampleDailyMACD = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;
    const groups = new Map();
    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;
      let key;
      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }
      if (interval === "1mo") key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      if (interval === "3mo")
        key = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];
      return {
        date: last.date,
        MACD: last.MACD,
        signal: last.signal,
        histogram: group.reduce((sum, g) => sum + (g.histogram ?? 0), 0),
      };
    });
  };

  const selectedData = useMemo(() => {
    if (timeFrame === "min") {
      if (!Array.isArray(macdChartDataMin)) return [];
      if (interval === "1m") return macdChartDataMin;
      if (interval === "3m") return resampleMinuteMACD(macdChartDataMin, 3);
      if (interval === "5m") return resampleMinuteMACD(macdChartDataMin, 5);
      return macdChartDataMin;
    }
    if (!Array.isArray(macdChartData)) return [];
    if (interval === "1d") return macdChartData;
    if (interval === "1w") return resampleDailyMACD(macdChartData, "1w");
    if (interval === "1mo") return resampleDailyMACD(macdChartData, "1mo");
    if (interval === "3mo") return resampleDailyMACD(macdChartData, "3mo");
    return macdChartData;
  }, [timeFrame, interval, macdChartDataMin, macdChartData]);

  const minPriceBase = intradayData?.closeSeries || [];
  const dayPriceBase = indicatorData?.closeSeries || [];

  const resampleMinutePrice = (data, step) => {
    if (!Array.isArray(data) || step === 1) return data;
    const result = [];
    for (let i = 0; i < data.length; i += step) {
      const chunk = data.slice(i, i + step);
      if (chunk.length < step) continue;
      const last = chunk[chunk.length - 1];
      result.push({ date: last.date, close: last.close });
    }
    return result;
  };

  const resampleDailyPrice = (data, interval) => {
    if (!Array.isArray(data) || interval === "1d") return data;
    const groups = new Map();
    data.forEach((row) => {
      const d = new Date(row.date);
      if (isNaN(d.getTime())) return;
      let key;
      if (interval === "1w") {
        const year = d.getFullYear();
        const week = Math.ceil(
          ((d - new Date(year, 0, 1)) / 86400000 +
            new Date(year, 0, 1).getDay() +
            1) /
            7,
        );
        key = `${year}-W${week}`;
      }
      if (interval === "1mo") key = `${d.getFullYear()}-${d.getMonth() + 1}`;
      if (interval === "3mo")
        key = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(row);
    });
    return Array.from(groups.values()).map((group) => {
      const last = group[group.length - 1];
      return { date: last.date, close: last.close };
    });
  };

  const selectedPriceData = useMemo(() => {
    if (timeFrame === "min") {
      const stepMap = { "1m": 1, "3m": 3, "5m": 5 };
      return resampleMinutePrice(minPriceBase, stepMap[interval] || 1);
    }
    return resampleDailyPrice(dayPriceBase, interval);
  }, [timeFrame, interval, minPriceBase, dayPriceBase]);

  // ============================================================
  // CROSSOVERS
  // ============================================================
  const detectCrossovers = (data, priceData) => {
    const signals = [];
    for (let i = 1; i < data.length; i++) {
      const prev = data[i - 1],
        curr = data[i];
      if (
        prev.MACD == null ||
        prev.signal == null ||
        curr.MACD == null ||
        curr.signal == null
      )
        continue;
      const price = priceData?.[i]?.close;
      if (prev.MACD < prev.signal && curr.MACD > curr.signal)
        signals.push({
          type: "BUY",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
      if (prev.MACD > prev.signal && curr.MACD < curr.signal)
        signals.push({
          type: "SELL",
          date: curr.date,
          index: i,
          price,
          macd: curr.MACD,
          signal: curr.signal,
        });
    }
    return signals;
  };

  useEffect(() => {
    if (!macdChartDataMin?.length || !minPriceBase?.length) return;
    const signals = detectCrossovers(macdChartDataMin, minPriceBase);
    setCrossovers((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(signals)) return prev;
      return signals;
    });
  }, [macdChartDataMin, minPriceBase]);

  const calculateTradePerformance = (signals, priceData) => {
    const trades = [];
    let activeTrade = null;
    for (const signal of signals) {
      if (signal.type === "BUY" && !activeTrade) {
        activeTrade = {
          entryDate: signal.date,
          entryPrice: signal.price,
          highestPrice: signal.price,
          status: "OPEN",
        };
      }
      if (activeTrade && signal.price > activeTrade.highestPrice) {
        activeTrade.highestPrice = signal.price;
      }
      if (signal.type === "SELL" && activeTrade) {
        const pnl =
          ((signal.price - activeTrade.entryPrice) / activeTrade.entryPrice) *
          100;
        const maxProfit =
          ((activeTrade.highestPrice - activeTrade.entryPrice) /
            activeTrade.entryPrice) *
          100;
        trades.push({
          ...activeTrade,
          exitDate: signal.date,
          exitPrice: signal.price,
          pnl: pnl.toFixed(2),
          maxProfit: maxProfit.toFixed(2),
          status: "CLOSED",
        });
        activeTrade = null;
      }
    }
    return trades;
  };

  const trades = useMemo(
    () => calculateTradePerformance(crossovers, selectedPriceData),
    [crossovers, selectedPriceData],
  );

  // ============================================================
  // WATCHLIST
  // ============================================================
  const isActive = isInWatchlist(companyCode);

  const handleAdd = async (symbol) => {
    const exists = watchlist?.some(
      (item) =>
        item.symbol?.trim().toUpperCase() === symbol?.trim().toUpperCase(),
    );
    try {
      if (exists) await removeFromWatchlist({ userID, symbol });
      else await addToWatchlist({ userID, symbol, securityId });
    } catch (err) {
      console.error("Toggle failed:", err);
    }
  };

  // ============================================================
  // PDF
  // ============================================================
  const buildPDFPayload = () => ({
    symbol: companyCode,
    companyName: stockName?.company,
    price: {
      ltp: quote?.last_price,
      open: stock?.open,
      high: stock?.high,
      low: stock?.low,
      close: prev_close,
      percentChange: Number(percent_change),
    },
    forecast: {
      targetPrice: target_price,
      upsidePercent: Number(projectChange),
    },
    technicals: { rsi: latestRSIValue, macd: latestMACDValue, pcr: pcr?.value },
    fundamentals: {
      peRatio,
      marketCap: marketCapData?.marketCap,
      faceValue: marketCapData?.faceValue,
    },
    returns: {
      oneWeek: Number(oneWeekReturn),
      oneMonth: Number(oneMonthReturn),
      ytd: Number(ytdReturn),
      oneYear: Number(oneYearReturn),
    },
    vwapAnalysis: {
      vwap: latestVWAP ? Number(latestVWAP.toFixed(2)) : null,
      ltp: ltpValue,
      difference: vwap ? Number((ltpValue - vwap).toFixed(2)) : null,
      percentFromVWAP: vwap
        ? Number((((ltpValue - vwap) / vwap) * 100).toFixed(2))
        : null,
      signal,
    },
    valuation: {
      marketCap: marketCapData?.marketCap,
      enterpriseValue: statisticsData?.enterpriseValue?.fmt,
      forwardPE: statisticsData?.forwardPE?.fmt,
      priceToBook: statisticsData?.priceToBook?.fmt,
      enterpriseToRevenue: statisticsData?.enterpriseToRevenue?.fmt,
    },
    profitability: {
      profitMargin: statisticsData?.profitMargins?.fmt,
      earningsGrowthQoQ: statisticsData?.earningsQuarterlyGrowth?.fmt,
      trailingEPS: statisticsData?.trailingEps?.fmt,
      forwardEPS: statisticsData?.forwardEps?.fmt,
    },
    ownership: {
      insiderHolding: statisticsData?.heldPercentInsiders?.fmt,
      institutionalHolding: statisticsData?.heldPercentInstitutions?.fmt,
      floatShares: statisticsData?.floatShares?.fmt,
      sharesOutstanding: statisticsData?.sharesOutstanding?.fmt,
    },
    insight: {
      result: timeFrame === "min" ? result1 : result,
      prediction: timeFrame === "min" ? prediction1 : prediction,
      timeframe: timeFrame,
    },
    metadata: {
      generatedAt: new Date().toISOString(),
      generatedAtIST: new Date().toLocaleString("en-IN"),
      source: "Phro Trader",
    },
  });

  const handleDownloadReport = async () => {
    try {
      await downloadStockPDF(buildPDFPayload(), companyCode);
      window.alert("✅ Report downloaded successfully!");
    } catch (err) {
      window.alert("❌ PDF generation failed");
    }
  };

  // ============================================================
  // HELPER RENDERERS
  // ============================================================
  const formatVolume = (volume) => {
    if (volume == null) return "-";
    const num = Number(volume);
    return !Number.isFinite(num) ? "-" : num.toLocaleString("en-IN");
  };

  const formatMarketCapINR = (value) => {
    if (!value) return "--";
    if (value >= 1e7) return `₹${(value / 1e7).toFixed(2)} Cr`;
    if (value >= 1e5) return `₹${(value / 1e5).toFixed(2)} L`;
    return `₹${value.toLocaleString("en-IN")}`;
  };

  const getStatus = (value, ltp) => {
    if (!ltp || !value) return "";
    if (value > ltp) return "Above LTP";
    if (value < ltp) return "Below LTP";
    return "At LTP";
  };

  const getPriority = (value, ltp) => {
    if (!ltp || !value) return 1;
    const diff = Math.abs(value - ltp);
    if (diff < 5) return 3;
    if (diff < 15) return 2;
    return 1;
  };

  const renderStars = (count) =>
    [1, 2, 3].map((i) => (
      <span key={i} style={{ fontSize: "1rem", marginRight: "2px" }}>
        {i <= count ? (
          <FaStar style={{ color: "#FFD700" }} />
        ) : (
          <FaRegStar style={{ color: "#CBD5E1" }} />
        )}
      </span>
    ));

  const getSignalTooltip = (signal) => {
    if (!signal) return [];
    if (signal.includes("Fallback"))
      return [
        { id: 1, text: "Fallback Signal" },
        { id: 2, text: "Weak direction based on VWAP" },
        { id: 3, text: "No strong confirmation present" },
        { id: 4, text: "Use with caution" },
      ];
    if (signal.includes("Reclaim"))
      return [
        { id: 1, text: "VWAP Reclaim" },
        { id: 2, text: "Strong bullish signal" },
        { id: 3, text: "Price crossed above VWAP" },
      ];
    if (signal.includes("Pullback"))
      return [
        { id: 1, text: "VWAP Pullback" },
        { id: 2, text: "Medium strength signal" },
        { id: 3, text: "Price bouncing near VWAP" },
      ];
    return [
      { id: 1, text: "Trading Signal" },
      { id: 2, text: "Based on strategy conditions" },
    ];
  };

  const InfoTooltip = ({ text }) => {
    if (!Array.isArray(text)) return null;
    return (
      <span className="table-tooltip-wrapper">
        <span style={{ color: "black" }}> ⓘ</span>
        <div className="table-tooltip-content">
          {text.map((item) => (
            <div key={item.id} className="tooltip-line">
              {item.text}
            </div>
          ))}
        </div>
      </span>
    );
  };

  const stockAISummaryPayload = {
    symbol: companyCode,
    companyName: stockName?.company,
    price: {
      ltp: quote?.last_price,
      open: stock?.open,
      close: prev_close,
      high: stock?.high,
      low: stock?.low,
      percentChange: percent_change,
    },
    forecast: {
      targetPrice: target_price,
      targetLow: target_low,
      targetHigh: target_high,
      upsidePercent: projectChange,
    },
    technicals: { rsi: latestRSIValue, macd: latestMACDValue, pcr: pcr?.value },
    fundamentals: {
      peRatio,
      faceValue: marketCapData?.faceValue,
      marketCap: marketCapData?.marketCap,
    },
    returns: {
      oneWeek: oneWeekReturn,
      oneMonth: oneMonthReturn,
      ytd: ytdReturn,
      oneYear: oneYearReturn,
      threeYear: threeYearReturn,
      fiveYear: fiveYearReturn,
    },
    meta: { timeframe: timeFrame, lastUpdated },
  };

  const intradayVwap = intradayData;

  // ============================================================
  // JSX — identical to your original, no changes
  // ============================================================
  return (
    <>
      <div className="last-updated-head">
        {lastUpdated && <span>As on {lastUpdated}</span>}
        <FiRefreshCw
          onClick={handleRefresh}
          size={18}
          style={{ cursor: "pointer", transition: "0.2s" }}
          onMouseOver={(e) => (e.target.style.color = "#2fd18a")}
          onMouseOut={(e) => (e.target.style.color = "#9ca3af")}
        />
      </div>

      {!liveReady && !error && (
        <p
          style={{
            fontSize: "1rem",
            fontWeight: "700",
            display: "flex",
            gap: "1rem",
          }}
        >
          Fetching Live data...
          <span style={{ paddingLeft: "10px", marginTop: "5px" }}>
            <FaSpinner />
          </span>
        </p>
      )}

      {error && (
        <div
          style={{
            marginTop: "20px",
            padding: "14px",
            borderLeft: "5px solid #dc2626",
            background: "#fee2e2",
            color: "#7f1d1d",
            fontWeight: 600,
            borderRadius: "8px",
          }}
        >
          {error}
        </div>
      )}

      {liveReady && quote && (
        <>
          <div className={`page-container ${chatOpen ? "shrink" : ""}`}>
            <div className="page-inner-container">
              <div className="stock-detail-card">
                <div className="analysis-container">
                  <div className="analysis-header">
                    <div className="analysis-head-name">
                      <div className="analysis-head-div">
                        <h2 className="stock-title">{stockName?.company}</h2>
                        <span className="stock-tag">{companyCode}</span>
                      </div>
                      <div className="price-chip-head">
                        <div className="price-chip">
                          <p className="label">LTP</p>
                          <h3 className="value" style={{ color: "#2563eb" }}>
                            ₹{quote?.last_price}
                          </h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">% Change</p>
                          <h3
                            className="value positive"
                            style={{
                              color: percent_change > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {percent_change}%
                          </h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">Open</p>
                          <h3 className="value">₹{stock?.open}</h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">Close</p>
                          <h3 className="value">₹{prev_close}</h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">Forecast Price</p>
                          <h3
                            className="value"
                            style={{
                              color:
                                Number.isFinite(target_price) &&
                                target_price > quote?.last_price
                                  ? "#249966"
                                  : "#f87171",
                            }}
                          >
                            {target_price && Number.isFinite(target_price)
                              ? `₹${target_price}`
                              : "—"}
                          </h3>
                        </div>
                        <div className="price-chip">
                          <p className="label">VWAP</p>
                          <h3 className="value">
                            ₹{latestVWAP?.toFixed(2) || "--"}
                          </h3>
                        </div>
                      </div>
                    </div>
                    <div className="action-buttons">
                      <button
                        className="rec-btn"
                        onClick={handleDownloadReport}
                      >
                        Download Report
                      </button>
                      <span
                        className={`watchlist-icon ${isActive ? "active" : "inactive"}`}
                        onClick={() => handleAdd(companyCode)}
                      >
                        {isActive ? (
                          <FaStar className="star" />
                        ) : (
                          <FiStar className="star" />
                        )}
                      </span>
                    </div>
                  </div>

                  {/* INSIGHTS */}
                  <div className="outer-insight-box">
                    <div
                      className="insight-box"
                      style={{
                        borderLeft:
                          timeFrame === "min"
                            ? `4px solid ${color1}`
                            : `4px solid ${color}`,
                      }}
                    >
                      <div className="insight-head-div">
                        <p className="insight-head">Phro Trader Insights</p>
                      </div>
                      <div className="vwap-rsi-signal">
                        <div className="sub-insight vwap-card">
                          <div className="vwap-header">
                            <div
                              className="vwap-sub-header"
                              style={{ marginBottom: "12px" }}
                            >
                              <span>RSI & MACD BASED SIGNAL</span>
                              <select
                                value={timeFrame}
                                onChange={(e) => setTimeFrame(e.target.value)}
                                style={{
                                  background: "inherit",
                                  fontWeight: "600",
                                  color: "black",
                                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                                  padding: "5px 7px",
                                  borderRadius: "10px",
                                }}
                              >
                                <option value="min">Minute</option>
                                <option value="day">Day</option>
                              </select>
                            </div>
                          </div>
                          <div className="insight-sub-div">
                            <div
                              className="insight-text"
                              style={{
                                color: timeFrame === "min" ? color1 : color,
                              }}
                            >
                              {timeFrame === "min" ? result1 : result}
                            </div>
                            <button
                              className="insight-tag"
                              style={{
                                backgroundColor:
                                  timeFrame === "min" ? color1 : color,
                                color: "white",
                              }}
                            >
                              {timeFrame === "min" ? prediction1 : prediction}
                            </button>
                          </div>
                        </div>

                        <div className="vwap-card">
                          <div className="vwap-header">
                            <div className="vwap-sub-header">
                              <span>VWAP MOMENTUM SIGNAL</span>
                              <span className="intraday-badge">Intraday</span>
                            </div>
                            <div className="insight-sub-div">
                              <span style={{ alignSelf: "center" }}>
                                LTP - VWAP:
                                <span
                                  style={{
                                    color: diff > 0 ? "#16a34a" : "#dc2626",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {diff > 0 ? " +" : ""}
                                  {diff.toFixed(2)} (
                                  {percent > 0 ? `+${percent}` : `${percent}`}%)
                                </span>
                              </span>
                              <button
                                className={`insight-tag vwap-badge ${signal === "Bullish" ? "green" : signal === "Bearish" ? "red" : "grey"}`}
                              >
                                {signal}
                              </button>
                            </div>
                          </div>
                          <p className="vwap-text">
                            {signal === "Bullish" &&
                              `Price is above VWAP (₹${vwap?.toFixed(2)}) — bullish intraday strength.`}
                            {signal === "Bearish" &&
                              `Price is below VWAP (₹${vwap?.toFixed(2)}) — bearish pressure likely.`}
                            {signal === "Neutral" &&
                              `Price is near VWAP (₹${vwap?.toFixed(2)}) — wait for breakout.`}
                          </p>
                        </div>

                        <div className="vwap-card">
                          <div className="vwap-header">
                            <div className="sub-vwap-head">
                              <div className="vwap-sub-header">
                                <span>UT BOT SIGNAL</span>
                              </div>
                              <button
                                className={`insight-tag vwap-badge ${lastUTSignal?.text === "BUY" ? "green" : lastUTSignal?.text === "SELL" ? "red" : "grey"}`}
                              >
                                {lastUTSignal?.text === "BUY"
                                  ? "Bullish"
                                  : "Bearish"}
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="vwap-card">
                          <div className="vwap-header">
                            <div className="sub-vwap-head">
                              <div className="vwap-sub-header">
                                <span>BB SIGNAL</span>
                              </div>
                              <button
                                className={`insight-tag vwap-badge ${bbgSignal === "BUY" ? "green" : bbgSignal === "SELL" ? "red" : "grey"}`}
                              >
                                {bbgSignal === "BUY" ? "Bullish" : "Bearish"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* NEAR PIVOTS */}
                    <div className="tp-card tp-insight">
                      <h4 className="tp-title insight-critical-title">
                        <span className="tp-insight-span">
                          Near Pivots ⭐ ⭐ ⭐{" "}
                          <span style={{ color: "#2563eb" }}>
                            LTP - ₹{quote?.last_price}
                          </span>
                        </span>
                      </h4>
                      <div className="near-pivot-grid">
                        <div className="np-card np-pivot-row">
                          <span className="np-label">Key Resistance</span>
                          <span className="np-value red">
                            {nearestResistance?.value
                              ? `₹${nearestResistance.value.toFixed(2)}`
                              : "All Broken"}
                          </span>
                        </div>
                        <div className="np-card">
                          <span className="np-label">Key Support</span>
                          <span className="np-value green">
                            ₹{nearestSupport?.value?.toFixed(2)}
                          </span>
                        </div>
                        <div className="np-card">
                          <span className="np-label">VWAP</span>
                          <span className="np-value blue">
                            ₹{latestVWAP?.toFixed(2)}
                          </span>
                        </div>
                        <div className="np-card">
                          <span className="np-label">Stop Loss</span>
                          <span className="np-value gray">
                            ₹{analysis?.stopLoss}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ENTRY/EXIT/PIVOT TABLE — identical to your original */}
                  <div className="factor-box">
                    <div className="vwap-entry-exit">
                      <div className="factor-box vwap-factor-box">
                        <h4>Vwap Entry Exit Stop Loss</h4>
                        <div className="tp-row-container">
                          <div className="tp-card tp-entry">
                            <h4 className="tp-title">ENTRY ZONE</h4>
                            <div className="tp-entry-content">
                              <div className="tp-entry-top">
                                <h2
                                  className={`tp-signal tp-${analysis?.entry?.type}`}
                                >
                                  {analysis?.entry?.label}
                                </h2>
                              </div>
                              <span className="tp-vwap-inline">
                                <span>VWAP</span>₹{latestVWAP?.toFixed(2)}
                              </span>
                              <span className="tp-inline-text">
                                {analysis?.entry?.msg}
                              </span>
                            </div>
                          </div>
                          <div className="tp-card tp-target">
                            <h4 className="tp-title">EXIT TARGETS</h4>
                            <div className="tp-target-block">
                              <div className="tp-target-row">
                                <span className="tp-target-label">
                                  {nearestResistance?.key} (Resistance)
                                </span>
                                <span className="tp-target-value">
                                  {nearestResistance?.value
                                    ? `₹${nearestResistance.value.toFixed(2)}`
                                    : "All Broken"}
                                </span>
                              </div>
                              <p className="tp-target-type">(Primary)</p>
                            </div>
                            <div className="tp-target-block">
                              <div className="tp-target-row">
                                <span className="tp-target-label">
                                  {nearestSupport?.key} (Support)
                                </span>
                                <span className="tp-target-value">
                                  ₹{nearestSupport?.value?.toFixed(2)}
                                </span>
                              </div>
                              <p className="tp-target-type">(Aggressive)</p>
                            </div>
                          </div>
                          <div className="tp-card tp-stop">
                            <h4 className="tp-title">STOP LOSS</h4>
                            <div className="tp-stop-content">
                              <h2 className="tp-stop-value">
                                ₹{analysis?.stopLoss}
                              </h2>
                              <p className="tp-subtext">Based on VWAP</p>
                            </div>
                          </div>
                          <div className="tp-card tp-critical-card">
                            <h4 className="tp-title">SUMMARY</h4>
                            <div className="tp-summary-box">
                              {analysis?.signal && (
                                <p className="tp-signal-text buy">
                                  {analysis.signal}
                                  <InfoTooltip
                                    text={getSignalTooltip(analysis?.signal)}
                                  />
                                </p>
                              )}
                              {analysis?.exit && (
                                <p className="tp-signal-text sell">
                                  {analysis.exit}
                                </p>
                              )}
                              <span className="tp-vwap-inline">
                                <span>{analysis?.pythonTrend}</span>
                              </span>
                              <span className="tp-inline-text">
                                {analysis?.traderNote}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="max-option">
                        <div className="option-chain-div">
                          <p>Option Chain</p>
                          <div className="option-chain-inner-div">
                            <button
                              className="rec-btn"
                              onClick={() =>
                                navigate(`/optionpage/${companyCode}`)
                              }
                            >
                              View
                            </button>
                          </div>
                        </div>
                        <div className="option-chain-div">
                          <p>Max Pain</p>
                          <div className="option-chain-inner-div">
                            <button
                              className="rec-btn"
                              onClick={() =>
                                navigate(`/maxpainpage/${companyCode}`)
                              }
                            >
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* PIVOT TABLE */}
                    <div className="factor-box">
                      <h4 className="pivot-level-head">
                        Pivot Levels
                        <span
                          style={{ display: "flex", justifyContent: "end" }}
                        >
                          <div className="chart-select">
                            <label>Interval</label>
                            <select
                              value={resistTimeFrame}
                              onChange={(e) =>
                                setResistTimeFrame(e.target.value)
                              }
                              style={{
                                background: "inherit",
                                fontWeight: "600",
                                color: "black",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                                padding: "5px 7px",
                                borderRadius: "10px",
                              }}
                            >
                              <option value="week">Weekly</option>
                              <option value="day">Day</option>
                            </select>
                          </div>
                        </span>
                      </h4>
                      <div className="pivot-container">
                        <table className="pivot-table ma-table">
                          <thead>
                            <tr>
                              <th>Pivot</th>
                              <th>Priority</th>
                              <th>Status</th>
                              <th>Classic</th>
                              <th>Fibonacci</th>
                              <th>Camarilla</th>
                              <th>Woodie</th>
                              <th>DM</th>
                            </tr>
                          </thead>
                          <tbody>
                            {levels?.map((level) => {
                              const classicValue =
                                resistanceDataToRender?.classic?.[level?.key];
                              const status = getStatus(classicValue, ltp);
                              const priority = getPriority(classicValue, ltp);
                              return (
                                <tr key={level?.key}>
                                  <td className="pivot-td-row">
                                    <span>
                                      {level?.key} ({level?.label})
                                    </span>
                                  </td>
                                  <td
                                    className="pivot-td-row"
                                    style={{ textAlign: "center" }}
                                  >
                                    <p className="render-stars">
                                      {renderStars(priority)}
                                    </p>
                                  </td>
                                  <td
                                    className="pivot-td-row"
                                    style={{
                                      fontWeight: "600",
                                      color:
                                        status === "Above LTP"
                                          ? "#dc2626"
                                          : status === "Below LTP"
                                            ? "#16a34a"
                                            : "#6b7280",
                                    }}
                                  >
                                    <span>{status}</span>
                                  </td>
                                  <td className="pivot-td-row">
                                    <span>
                                      {classicValue?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                  <td className="pivot-td-row">
                                    <span>
                                      {resistanceDataToRender?.fib?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                  <td>
                                    <span>
                                      {resistanceDataToRender?.cam?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                  <td>
                                    <span>
                                      {resistanceDataToRender?.woodie?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                  <td>
                                    <span>
                                      {resistanceDataToRender?.demark?.[
                                        level?.key
                                      ]?.toFixed(2) ?? "—"}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* PRICE SUMMARY */}
                  <div className="price-summary">
                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">Forecast Range</div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="Forecast Range"
                            low={target_low}
                            high={target_high}
                            ltp={target_price}
                            variant={variant}
                            price={quote?.last_price}
                            type="target"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">Day Range</div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="Day Range"
                            low={stock?.low}
                            high={stock?.high}
                            ltp={quote?.last_price}
                            price={quote?.last_price}
                            variant={variant}
                            type="price"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="price-scales">
                      <div className="scale-block">
                        <div className="label">52 Week Range</div>
                        <div className="range-svg-wrapper">
                          <RangeBarSVG
                            title="52 Week Range"
                            low={weekLow}
                            high={weekHigh}
                            ltp={quote?.last_price}
                            price={quote?.last_price}
                            variant={variant}
                            type="price"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* RETURNS */}
                  <div className="factor-box">
                    <h4>Wealth Creator/Destroyer</h4>
                    <div className="return-box">
                      {[
                        { label: "1W", value: oneWeekReturn },
                        { label: "1M", value: oneMonthReturn },
                        { label: "YTD", value: ytdReturn },
                        { label: "1Y", value: oneYearReturn },
                        { label: "3Y", value: threeYearReturn },
                        { label: "5Y", value: fiveYearReturn },
                      ].map(({ label, value }) => (
                        <div key={label} className="return-label">
                          <p className="return-p label">{label}</p>
                          <div
                            className={`return-value ${value > 0 ? "return-positive" : "return-negative"}`}
                          >
                            {value > 0 ? "+" : ""}
                            {value}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* TECHNICAL + FUNDAMENTAL */}
                  <div className="factor-wrapper">
                    <div className="factor-box">
                      <h4>Technical Factors</h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">RSI</p>
                          <span
                            style={{
                              color: latestRSIValue > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {latestRSIValue?.toFixed(2)}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">MACD</p>
                          <span
                            style={{
                              color:
                                latestMACDValue > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {latestMACDValue?.toFixed(2)}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Volume</p>
                          <span>{formatVolume(quote?.volume)}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">PCR</p>
                          <span>{pcr?.value}</span>
                        </div>
                      </div>
                    </div>
                    <div className="factor-box">
                      <h4>Fundamental Factors</h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">% Upside</p>
                          <span
                            style={{
                              color: projectChange > 0 ? "#249966" : "#f87171",
                            }}
                          >
                            {projectChange}%
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Forecast Price</p>
                          <span>₹{target_price}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">P/E Ratio</p>
                          <span>{peRatio}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Face Value</p>
                          <span>₹{marketCapData?.faceValue}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* VALUATION */}
                  <div className="val-1">
                    <div className="factor-box">
                      <h4>Valuation Measures</h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">Market Cap</p>
                          <span>
                            {formatMarketCapINR(marketCapData?.marketCap)}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Enterprise Value</p>
                          <span>{statisticsData?.enterpriseValue?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Forward P/E</p>
                          <span>{statisticsData?.forwardPE?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Price to Book</p>
                          <span>{statisticsData?.priceToBook?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Enterprise/Revenue</p>
                          <span>
                            {statisticsData?.enterpriseToRevenue?.fmt}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="factor-wrapper">
                    <div className="factor-box">
                      <h4>Profitability & Growth</h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">Profit Margin</p>
                          <span>{statisticsData?.profitMargins?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Earnings Growth (QoQ)</p>
                          <span>
                            {statisticsData?.earningsQuarterlyGrowth?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Trailing EPS</p>
                          <span>{statisticsData?.trailingEps?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Forward EPS</p>
                          <span>{statisticsData?.forwardEps?.fmt}</span>
                        </div>
                      </div>
                    </div>
                    <div className="factor-box">
                      <h4>Ownership & Risk</h4>
                      <div className="inner-factor-box">
                        <div className="factor-row">
                          <p className="label">Insider Holding %</p>
                          <span>
                            {statisticsData?.heldPercentInsiders?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Institutional Holding %</p>
                          <span>
                            {statisticsData?.heldPercentInstitutions?.fmt}
                          </span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Float Shares</p>
                          <span>{statisticsData?.floatShares?.fmt}</span>
                        </div>
                        <div className="factor-row">
                          <p className="label">Shares Outstanding</p>
                          <span>{statisticsData?.sharesOutstanding?.fmt}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* MOVING AVERAGES */}
                  <div className="factor-box ma-factor-box">
                    <div className="ma-panel">
                      <h4>Moving Averages</h4>
                      <div className="ma-div">
                        <Gauge
                          buy={summary?.finalBuy}
                          sell={summary?.finalSell}
                        />
                        <table className="ma-table pivot-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Simple</th>
                              <th></th>
                              <th>Exponential</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows?.length > 0 ? (
                              rows?.map((row) => (
                                <tr key={row?.name}>
                                  <td>
                                    <span>{row?.name}</span>
                                  </td>
                                  <td>
                                    <span className="ma-value">
                                      {row?.smaValue.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={
                                        row.smaTrend === "Buy"
                                          ? "ma-trend-buy"
                                          : "ma-trend-sell"
                                      }
                                    >
                                      {row?.smaTrend}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="ma-value">
                                      {row?.emaValue?.toFixed(2)}
                                    </span>
                                  </td>
                                  <td>
                                    <span
                                      className={
                                        row?.emaTrend === "Buy"
                                          ? "ma-trend-buy"
                                          : "ma-trend-sell"
                                      }
                                    >
                                      {row?.emaTrend}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan={5}>
                                  <span>Loading MA Data...</span>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* ORDER BOOK */}
                  <div className="factor-box">
                    <h4>Order Book</h4>
                    <table className="orderbook-table">
                      <thead>
                        <tr>
                          <th>Qty</th>
                          <th>Bid (₹)</th>
                          <th>Ask (₹)</th>
                          <th>Qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({
                          length: Math.max(
                            depth?.buy?.length || 0,
                            depth?.sell?.length || 0,
                          ),
                        }).map((_, idx) => {
                          const buy = depth?.buy?.[idx],
                            sell = depth?.sell?.[idx];
                          return (
                            <tr key={idx}>
                              <td className="buy-qty">
                                {buy ? buy?.quantity.toLocaleString() : "-"}
                              </td>
                              <td className="buy-price">
                                {buy ? buy.price.toFixed(2) : "-"}
                              </td>
                              <td className="sell-price">
                                {sell ? sell.price.toFixed(2) : "-"}
                              </td>
                              <td className="sell-qty">
                                {sell ? sell?.quantity.toLocaleString() : "-"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <div className="depth-summary">
                      <div className="depth-bar">
                        <div
                          className="buy-bar"
                          style={{ width: `${buyPercent}%` }}
                        />
                        <div
                          className="sell-bar"
                          style={{ width: `${sellPercent}%` }}
                        />
                      </div>
                      <div className="depth-text">
                        <span className="buy-text">
                          {buyPercent?.toFixed(2)}% Buy
                          <span
                            style={{ marginLeft: "5px" }}
                          >{`(${totalBuyQty?.toLocaleString()})`}</span>
                        </span>
                        <span className="total-text">
                          Total Quantity
                          <strong
                            style={{ marginLeft: "5px" }}
                          >{`(${totalQty.toLocaleString()})`}</strong>
                        </span>
                        <span className="sell-text">
                          {sellPercent?.toFixed(2)}% Sell
                          <span
                            style={{ marginLeft: "5px" }}
                          >{`(${totalSellQty?.toLocaleString()})`}</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* MACD CROSSOVER TABLE */}
                  <div className="factor-box">
                    <h4>MACD Crossover</h4>
                    <table className="ma-table pivot-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Date</th>
                          <th>Price</th>
                          <th>MACD</th>
                          <th>Signal</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[...crossovers].reverse().map((row, i) => (
                          <tr key={i}>
                            <td>
                              <span
                                style={{
                                  color:
                                    row.type === "BUY" ? "#22c55e" : "#ef4444",
                                  fontWeight: "bold",
                                }}
                              >
                                {row.type}
                              </span>
                            </td>
                            <td>{row.date}</td>
                            <td>₹{row.price?.toFixed(2)}</td>
                            <td>{row.macd?.toFixed(2)}</td>
                            <td>{row.signal?.toFixed(2)}</td>
                            <td>
                              {row.type === "BUY"
                                ? "MACD Bullish Crossover"
                                : "MACD Bearish Crossover"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* TRADE PERFORMANCE TABLE */}
                  <div className="factor-box">
                    <table className="ma-table pivot-table">
                      <thead>
                        <tr>
                          <th>Entry</th>
                          <th>Buy</th>
                          <th>Exit</th>
                          <th>Sell</th>
                          <th>P/L %</th>
                          <th>Max Profit %</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((trade, idx) => (
                          <tr key={idx}>
                            <td>{trade.entryDate}</td>
                            <td>₹{trade.entryPrice?.toFixed(2)}</td>
                            <td>{trade.exitDate || "-"}</td>
                            <td>
                              {trade.exitPrice
                                ? `₹${trade.exitPrice.toFixed(2)}`
                                : "-"}
                            </td>
                            <td
                              style={{
                                color:
                                  Number(trade.pnl) >= 0
                                    ? "#22c55e"
                                    : "#ef4444",
                              }}
                            >
                              {trade.pnl}%
                            </td>
                            <td>{trade.maxProfit}%</td>
                            <td>{trade.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* CHARTS */}
              {macdChartData && macdChartData.length > 0 && (
                <div className="macd-chart-graph">
                  <div className="chart-top-heading">
                    <h3>Price, MACD Line, Signal & Histogram</h3>
                    <div className="chart-select">
                      <label>Interval</label>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <select
                          value={timeFrame}
                          onChange={(e) => setTimeFrame(e.target.value)}
                          style={{
                            background: "inherit",
                            fontWeight: "600",
                            color: "black",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                            padding: "5px 7px",
                            borderRadius: "10px",
                          }}
                        >
                          <option value="min">Minute</option>
                          <option value="day">Day</option>
                        </select>
                        <select
                          value={interval}
                          onChange={(e) => setInterval(e.target.value)}
                          style={{
                            background: "inherit",
                            fontWeight: "600",
                            color: "black",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                            padding: "5px 7px",
                            borderRadius: "10px",
                          }}
                        >
                          {timeFrame === "min" && (
                            <>
                              <option value="1m">1 Minute</option>
                              <option value="3m">3 Minutes</option>
                              <option value="5m">5 Minutes</option>
                            </>
                          )}
                          {timeFrame === "day" && (
                            <>
                              <option value="1d">1 Day</option>
                              <option value="1w">1 Week</option>
                              <option value="1mo">1 Month</option>
                              <option value="3mo">3 Months</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  <h3 className="trend-head">Macd Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={selectedData} syncId="stock-sync">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date, index) => {
                          if (!date) return "";
                          if (timeFrame === "min" && typeof date === "string") {
                            const timePart = date?.split(",")[1]?.trim();
                            if (!timePart) return "";
                            const [hhmm] = timePart.split(":");
                            const minute = timePart.split(":")[1];
                            return index % 10 === 0 ? `${hhmm}:${minute}` : "";
                          }
                          const d = new Date(date);
                          if (isNaN(d.getTime())) return "";
                          return index % 15 === 0
                            ? d?.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                            : "";
                        }}
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "none",
                          color: "#fff",
                        }}
                        labelFormatter={(label) => {
                          try {
                            if (!label) return "";
                            const d = new Date(label.replace(" ", "T"));
                            if (isNaN(d.getTime())) return label;
                            return label.includes(":")
                              ? d.toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: false,
                                })
                              : d.toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                });
                          } catch {
                            return label;
                          }
                        }}
                        formatter={(value, name) => {
                          if (value == null) return ["–", name];
                          return [
                            Number(value).toFixed(2),
                            name === "MACD"
                              ? "MACD"
                              : name === "signal"
                                ? "Signal"
                                : "Histogram",
                          ];
                        }}
                        labelStyle={{ color: "#fff", fontWeight: "bold" }}
                        itemStyle={{ color: "#fff" }}
                      />
                      <Legend
                        wrapperStyle={{ fontWeight: "bold" }}
                        payload={[
                          {
                            value: "Histogram (Bullish)",
                            type: "square",
                            color: "#2fd18a",
                          },
                          {
                            value: "Histogram (Bearish)",
                            type: "square",
                            color: "#f87171",
                          },
                          { value: "MACD", type: "line", color: "#2563eb" },
                          { value: "Signal", type: "line", color: "#f59e0b" },
                        ]}
                      />
                      <Bar
                        dataKey="histogram"
                        name="Histogram"
                        barSize={20}
                        shape={(props) => {
                          const { x, y, width, height, value } = props;
                          const barHeight = Math.abs(height);
                          return (
                            <rect
                              x={x}
                              y={value >= 0 ? y : y - barHeight}
                              width={width}
                              height={barHeight}
                              fill={value >= 0 ? "#2fd18a" : "#f87171"}
                            />
                          );
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="MACD"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={false}
                        name="MACD"
                      />
                      <Line
                        type="monotone"
                        dataKey="signal"
                        stroke="#f59e0b"
                        strokeWidth={2}
                        dot={false}
                        name="Signal"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>

                  <h3 className="trend-head">Price Trend</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={selectedPriceData} syncId="stock-sync">
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date, index) => {
                          if (!date) return "";
                          if (timeFrame === "min" && typeof date === "string") {
                            const timePart = date.split(",")[1]?.trim();
                            if (!timePart) return "";
                            const [hh, mm] = timePart.split(":");
                            return index % 10 === 0 ? `${hh}:${mm}` : "";
                          }
                          const d = new Date(date);
                          return index % 15 === 0
                            ? d.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                              })
                            : "";
                        }}
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                      />
                      <YAxis
                        tick={{ fontWeight: "bold", fontSize: 10 }}
                        axisLine={{ stroke: "#fff" }}
                        tickLine={{ stroke: "#fff" }}
                        domain={["auto", "auto"]}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#1f2937",
                          border: "none",
                          color: "#fff",
                        }}
                        labelFormatter={(label) => {
                          if (!label) return "";
                          const d = new Date(label.replace(" ", "T"));
                          if (isNaN(d.getTime())) return label;
                          return label.includes(":")
                            ? d.toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              })
                            : d.toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              });
                        }}
                        formatter={(value) => [
                          Number(value).toFixed(2),
                          "Price",
                        ]}
                        labelStyle={{ color: "#fff", fontWeight: "bold" }}
                        itemStyle={{ color: "#fff" }}
                      />
                      <Legend
                        wrapperStyle={{ fontWeight: "bold" }}
                        payload={[
                          { value: "Price", type: "line", color: "#22c55e" },
                        ]}
                      />
                      <Line
                        type="monotone"
                        dataKey="close"
                        stroke="#22c55e"
                        strokeWidth={2}
                        dot={false}
                        name="Close Price"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>

                  <div className="candle-chart-container">
                    <div className="chart-legend">
                      <h3 className="trend-head">Candles Trend</h3>
                      <div className="candles-color-map">
                        <span className="legend-item vwap">🟡 VWAP</span>
                        <span className="legend-item buy">🟢 BUY Signal</span>
                        <span className="legend-item sell">🔴 SELL Signal</span>
                      </div>
                    </div>
                    <CandleChart
                      intradayData={intradayVwap}
                      histData={indicatorData}
                      setLastUTSignal={setLastUTSignal}
                      width={"100%"}
                    />
                  </div>
                </div>
              )}

              {/* NEWS */}
              <div className="factor-box">
                <h4>News</h4>
                <div className="news-grid stock-news-detail">
                  {newsData?.body
                    ?.slice(0, visibleCount)
                    ?.map((item, index) => (
                      <a
                        key={index}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="news-card"
                      >
                        <div className="news-content">
                          <h3 className="news-title">{item.title}</h3>
                          <div className="news-timestamp">
                            <p className="news-time">
                              {new Date(item.pubDate).toLocaleString("en-IN")}
                            </p>
                            <p className="news-time">Source - {item.source}</p>
                          </div>
                        </div>
                      </a>
                    ))}
                </div>
                {visibleCount < newsData?.body?.length && (
                  <div className="view-more-container">
                    <button
                      className="rec-btn"
                      onClick={() => setVisibleCount((prev) => prev + 4)}
                    >
                      View More
                    </button>
                  </div>
                )}
              </div>
            </div>

            <ChatPanel2 stockContext={stockAISummaryPayload} />
            <AIFloatButton />
          </div>
        </>
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
      />
    </>
  );
};

export default StockCard;
